import { LineItemTypeOptions, TAX_CODE_OTHER_EXEMPT } from "../types/OcrTypes";
import { sanitizeSKU } from "../logic/OcrUtils";
import { v4 } from "uuid";
import { formatDate } from '../utils/DateUtils';
import { isAfter, isValid as isValidDate } from "date-fns";
/**
 * Unit must be a part of units to be considered valid, or be a close match.
 *
 * @param unit - Unit to validate against
 * @param units - Must be all lowercase
 */
export function getValidUnit(unit, units) {
    let lowerCaseUnit = unit.toLowerCase();
    // Check for some common short forms
    switch (lowerCaseUnit) {
        case 'ea':
            lowerCaseUnit = 'each';
            break;
        case 'cs':
            lowerCaseUnit = 'case';
            break;
        case 'pk':
        case 'pck':
            lowerCaseUnit = 'pack';
            break;
        case 'doz':
        case 'dz':
            lowerCaseUnit = 'dozen';
            break;
        case 'l':
            lowerCaseUnit = 'litre';
            break;
        case 'gal':
            lowerCaseUnit = 'gallon';
            break;
        case 'lbs':
            lowerCaseUnit = 'lb';
            break;
        default:
            break;
    }
    const validUnit = units.includes(lowerCaseUnit) ? lowerCaseUnit : undefined;
    return validUnit;
}
export function getLineItemsFromInvoiceData(ocrParsedLineItems) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const lineItems = [];
    if (ocrParsedLineItems) {
        for (let i = 0; i < ocrParsedLineItems.length; i++) {
            const curProduct = ocrParsedLineItems[i];
            const tax = { dollarAmount: 0, percent: 0 };
            let taxIsSet = false;
            // in this case the tax amount has been saved, so read it instead
            if (curProduct === null || curProduct === void 0 ? void 0 : curProduct.tax) {
                const parsedTotal = getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.amount);
                tax.dollarAmount = getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.tax);
                const calculatedSubtotal = (parsedTotal - tax.dollarAmount);
                taxIsSet = true;
                // protect against divide by zero NaN problems.
                if (calculatedSubtotal <= 0) {
                    tax.percent = 0;
                }
                else {
                    tax.percent = (tax.dollarAmount / calculatedSubtotal) * 100;
                }
            }
            else { // if tax hasn't been saved, try to calculate it google doesn't parse tax by default. I will try to uptrain the model and add the tax as a custom field for us.
                const unitPrice = getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.unitPrice);
                const quantity = getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.quantity);
                const weight = getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.weight);
                const total = getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.amount);
                let calculatedNetPrice = unitPrice * quantity;
                if (weight) {
                    calculatedNetPrice = weight * unitPrice;
                }
                const calculatedTaxAmount = total - calculatedNetPrice;
                let calculatedTaxPercent = 0;
                if (!isNaN(calculatedTaxAmount) && calculatedTaxAmount > 0) {
                    calculatedTaxPercent = calculatedNetPrice / calculatedTaxAmount;
                }
                tax.dollarAmount = calculatedTaxAmount;
                tax.percent = calculatedTaxPercent;
            }
            // round the tax dollar and percent off to 2 decimals
            tax.dollarAmount = valueToFixedNumber(tax.dollarAmount, 2);
            tax.percent = valueToFixedNumber(tax.percent, 2);
            lineItems.push({
                name: ((_a = curProduct === null || curProduct === void 0 ? void 0 : curProduct.description) === null || _a === void 0 ? void 0 : _a.value) || '',
                sku: sanitizeSKU((_c = (_b = curProduct === null || curProduct === void 0 ? void 0 : curProduct.productCode) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : ''),
                unit: ((_d = curProduct === null || curProduct === void 0 ? void 0 : curProduct.unit) === null || _d === void 0 ? void 0 : _d.value) || '',
                quantity: Number((_e = curProduct === null || curProduct === void 0 ? void 0 : curProduct.quantity) === null || _e === void 0 ? void 0 : _e.value) || 0,
                unitPrice: valueToFixedNumber(((_f = curProduct === null || curProduct === void 0 ? void 0 : curProduct.unitPrice) === null || _f === void 0 ? void 0 : _f.value) || 0),
                total: parseFloat(valueToFixedString(((_g = curProduct === null || curProduct === void 0 ? void 0 : curProduct.amount) === null || _g === void 0 ? void 0 : _g.value) || 0)),
                weight: Number(((_h = curProduct === null || curProduct === void 0 ? void 0 : curProduct.weight) === null || _h === void 0 ? void 0 : _h.value) || 0),
                removedFromOcrInvoice: (curProduct === null || curProduct === void 0 ? void 0 : curProduct.isRemoved) === true,
                taxCode: '99999',
                packageID: null,
                tax,
                taxIsSet,
                glCodeID: curProduct === null || curProduct === void 0 ? void 0 : curProduct.glCodeID,
                taxCodeID: curProduct === null || curProduct === void 0 ? void 0 : curProduct.taxCodeID,
                unitDeposit: curProduct === null || curProduct === void 0 ? void 0 : curProduct.unitDeposit,
                discount: getItemConfidenceAsNumber(curProduct === null || curProduct === void 0 ? void 0 : curProduct.discountAmount),
                type: (_j = curProduct.type) !== null && _j !== void 0 ? _j : LineItemTypeOptions.PRODUCT,
            });
        }
    }
    return lineItems;
}
/**
 * A fuzzier check that will do things like have an empty string and null be equal, or 0 and null, etc.
 * @param a value to check if is equal to b
 * @param b value to check if is equal to a
 * @returns True if they're equal in a fuzzier sense
 */
export function checkInputFieldEquality(a, b) {
    let aAsNum = null;
    let bAsNum = null;
    if (typeof a === 'string') {
        aAsNum = parseFloat(a);
    }
    else if (typeof a === 'number') {
        aAsNum = a;
    }
    if (typeof b === 'string') {
        bAsNum = parseFloat(b);
    }
    else if (typeof b === 'number') {
        bAsNum = b;
    }
    // special cases - if either is null and the matching values is 0 we count that as the same thing in this case
    if ((!a && bAsNum === 0) || (aAsNum === 0 && !b)) {
        return true;
    }
    if (!isNaN(aAsNum) && !isNaN(bAsNum)) {
        return aAsNum === bAsNum;
    }
    if (typeof a === 'string' && typeof b === 'string') {
        return a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();
    }
    // finally do a straight equality as a last effort
    return a === b;
}
/**
 *
 */
export function areLineItemsEqual(a, b) {
    var _a, _b, _c, _d;
    const keys = ['name', 'sku', 'glCodeID', 'unit', 'quantity', 'unitPrice', 'total', 'weight', 'taxCode', 'tax'];
    for (let i = 0; i < keys.length; i++) {
        const curKey = keys[i];
        if (curKey === 'tax') {
            return ((_a = a.tax) === null || _a === void 0 ? void 0 : _a.dollarAmount) === ((_b = b.tax) === null || _b === void 0 ? void 0 : _b.dollarAmount) && ((_c = a.tax) === null || _c === void 0 ? void 0 : _c.percent) === ((_d = b.tax) === null || _d === void 0 ? void 0 : _d.percent);
        }
        if (!checkInputFieldEquality(a[curKey], b[curKey])) {
            return false;
        }
    }
    return true;
}
function getItemConfidenceAsNumber(confidenceGroup, notFoundValue = 0) {
    const value = Number(confidenceGroup === null || confidenceGroup === void 0 ? void 0 : confidenceGroup.value);
    return value || notFoundValue;
}
export function valueToFixedNumber(value, fixedAmount = 2) {
    const stringified = valueToFixedString(value, fixedAmount);
    return Number(stringified);
}
export function valueToFixedString(value, fixedAmount = 2) {
    const num = Number(value);
    if (num) {
        return num.toFixed(fixedAmount);
    }
    return Number(0).toFixed(fixedAmount);
}
/**
 * Returns true if the given string can be parsed as a date
 */
export function isStringValidDate(stringData) {
    return !Number.isNaN(Date.parse(stringData));
}
export function reconcileTaxValues(lineItem, globalTaxPercent) {
    var _a;
    // if the line item doesn't have an exempt tax code it's subject to tax
    // In such a case if the dollarAmount is 0 (or less) then something is messed up and we have a good candidate to try and fix
    if (lineItem.linkedCatalogData && ((_a = lineItem.linkedCatalogData) === null || _a === void 0 ? void 0 : _a.taxCode) !== TAX_CODE_OTHER_EXEMPT.value && lineItem.primaryItemData.taxIsSet === false) {
        // in this case set the tax to the global value as a failsafe
        const taxDollarAmount = (lineItem === null || lineItem === void 0 ? void 0 : lineItem.primaryItemData.total) * globalTaxPercent;
        lineItem.primaryItemData.tax = { dollarAmount: taxDollarAmount, percent: globalTaxPercent * 100 };
        lineItem.primaryItemData.total += taxDollarAmount;
    }
}
export function isInvoiceInClosedAccountingPeriod(ocrInvoice) {
    if (!ocrInvoice || !ocrInvoice.isQBOEnabled) {
        return false;
    }
    // we need to ensure that accountingInfo is loaded
    if (!ocrInvoice.accountingInfo) {
        return true;
    }
    const issueDate = new Date(formatDate(ocrInvoice.topLevelData.issueDate));
    const bookCloseDate = ocrInvoice.accountingInfo.bookCloseDate && new Date(ocrInvoice.accountingInfo.bookCloseDate);
    return isValidDate(issueDate) && isValidDate(bookCloseDate) && !isAfter(issueDate, bookCloseDate);
}
/**
 * Find all line items which have matching GL Codes and Tax Codes and combine them
*/
export function consolidateByGLCode(matchedLineItems, taxCodes) {
    var _a, _b, _c;
    const consolidatedItems = [];
    // Process all line items
    for (let i = 0; i < matchedLineItems.length; i++) {
        const currentLine = matchedLineItems[i];
        const currentItem = currentLine.primaryItemData;
        // Skip removed line items
        if (currentLine.isRemoved)
            continue;
        // Check if this combo of GL Code + Tax Code has already been added
        let consolidatedIndex = consolidatedItems.findIndex((item) => item.primaryItemData.glCodeID === currentItem.glCodeID && item.primaryItemData.taxCodeID === currentItem.taxCodeID);
        if (consolidatedIndex < 0) {
            // Create the consolidated line item
            const taxCodeName = (_b = (_a = taxCodes === null || taxCodes === void 0 ? void 0 : taxCodes.find((t) => t.id === currentItem.taxCodeID)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : '';
            const name = `GL-${currentItem.glCodeID}-${taxCodeName}`;
            consolidatedItems.push({
                matchResult: "equal",
                type: "product",
                reactKey: v4(),
                primaryItemData: {
                    name,
                    sku: name,
                    quantity: 1,
                    unitPrice: 0,
                    weight: 0,
                    total: 0,
                    unitDeposit: 0,
                    discount: 0,
                    glCodeID: currentItem.glCodeID,
                    tax: {
                        dollarAmount: 0,
                        percent: 0,
                    },
                    taxCode: currentItem.taxCode,
                    taxCodeID: currentItem.taxCodeID,
                    unit: 'each',
                    categoryID: currentItem.categoryID,
                    type: (_c = currentItem.type) !== null && _c !== void 0 ? _c : LineItemTypeOptions.PRODUCT,
                }
            });
            consolidatedIndex = consolidatedItems.length - 1;
        }
        const consolidatedItem = consolidatedItems[consolidatedIndex];
        // Add this item's values to the consolidated sums
        if (currentItem.weight && parseFloat(currentItem.weight) > 0) {
            // If there is a weight, calculate price as unitPrice * weight
            consolidatedItem.primaryItemData.unitPrice += parseFloat(currentItem.unitPrice) * parseFloat(currentItem.weight);
        }
        else {
            // If when there is no weight, calculate price as unitPrice * quantity
            consolidatedItem.primaryItemData.unitPrice += parseFloat(currentItem.unitPrice) * parseFloat(currentItem.quantity);
        }
        consolidatedItem.primaryItemData.tax.dollarAmount += currentItem.tax.dollarAmount;
        consolidatedItem.primaryItemData.discount += parseFloat(currentItem.discount);
        consolidatedItem.primaryItemData.unitDeposit += currentItem.unitDeposit;
        // Set each line's total
        for (let i = 0; i < consolidatedItems.length; i++) {
            const item = consolidatedItems[i];
            item.primaryItemData.total = (item.primaryItemData.unitPrice + valueToFixedNumber(item.primaryItemData.unitDeposit)) * item.primaryItemData.quantity + item.primaryItemData.tax.dollarAmount - item.primaryItemData.discount;
        }
    }
    return consolidatedItems;
}
//# sourceMappingURL=OcrInvoiceHelpers.js.map